import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  IconButton,
} from "@mui/material";
import {
  stableSort,
  getComparator,
} from "../../../../../common-utilities/sortingUtils";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { DeleteTestLib } from "../../../../services/TestLibService";
import { NotificationManager } from "react-notifications";
import DeleteConfirmation from "../../../common/DeleteConfirmation";

const TestLibsTable = ({ testLibsData, count, onChange }) => {
  const columns = [
    "Test Code",
    "Test Category",
    "Parameter",
    "Formula Id",
    "Method Code",
    "Status",
    "Actions",
  ];
  const columnHeaders = [
    "testCode",
    "testCategory",
    "parameter",
    "formulaId",
    "methodCode",
    "testStatus",
  ];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [selectedTestCode, setSelectedTestCode] = useState(null);

  useEffect(() => {
    handleChange();
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChange = () => {
    onChange(page, rowsPerPage);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const DeleteTestLibrary = async (testCode) => {
    try {
      const response = await DeleteTestLib(testCode);
      if (response.results === true) {
        NotificationManager.success("Test library deleted.");
      } else {
        NotificationManager.error("Error deleting test libraries.");
      }
    } catch (err) {
      NotificationManager.error(
        "Error deleting test libraries. " + err,
        "Error"
      );
    } finally {
      setRowsPerPage(rowsPerPage + 1);
    }
  };

  const openEditTab = (testCode) => {
    window.open(
      "/vlp/maintenance-test-library-management/" + encodeURIComponent(testCode.trim()),
      "_blank"
    );
  };

  const renderActions = (testLib) => (
    <div style={{ minWidth: "100px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => openEditTab(testLib.testCode)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => openEditTab(testLib.testCode)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteConfirmationOpen(testLib.testCode)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  const handleDeleteConfirmationOpen = (testCode) => {
    setSelectedTestCode(testCode);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setSelectedTestCode(null);
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    await DeleteTestLibrary(selectedTestCode);
    handleDeleteConfirmationClose();
    handleChange();
  };

  return (
    <>
      <Paper style={{ marginRight: "20px" }}>
        <TableContainer>
          <Table sx={{ minWidth: 400 }} size="small" dense>
            <TableHead
              sx={{
                backgroundColor: (theme) => theme.palette.primary.main,
                color: "white",
              }}
            >
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell
                    key={column}
                    onClick={() =>
                      columnHeaders[index] &&
                      handleRequestSort(columnHeaders[index])
                    }
                    sx={{ cursor: columnHeaders[index] ? "pointer" : "default" }}
                  >
                    {columnHeaders[index] ? (
                      <TableSortLabel
                        active={orderBy === columnHeaders[index]}
                        direction={orderBy === columnHeaders[index] ? order : "asc"}
                        sx={{
                          color: "white !important",
                          "& .MuiTableSortLabel-icon": { color: "white !important" },
                        }}
                      >
                        {column}
                      </TableSortLabel>
                    ) : (
                      column
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {stableSort(testLibsData, getComparator(order, orderBy)).map((testLib, index) => (
                <TableRow
                  key={testLib.testCode}
                  sx={{
                    backgroundColor:
                      index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                  }}
                >
                  {columnHeaders.map((column) => (
                    <TableCell key={column}>{testLib[column]}</TableCell>
                  ))}
                  <TableCell>{renderActions(testLib)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        <DeleteConfirmation
          isOpen={isDeleteConfirmationOpen}
          onClose={handleDeleteConfirmationClose}
          onConfirm={handleDeleteConfirmationConfirm}
          title="Delete Confirmation"
          message="Are you sure you want to delete this test library?"
          color="red"
        />
      </Paper>
    </>
  );
};

export default TestLibsTable;
